import { useEffect, useMemo, useState } from 'react';
import AdminLayout from '../../components/layout/AdminLayout';
import { supabase } from '../../lib/supabase';

type SaasPlan = {
  id: string;
  codigo: string;
  nombre: string;
  descripcion: string | null;
  moneda: string;
  precio: number;
  periodo_dias: number;
  limite_usuarios: number | null;
  limite_sucursales: number | null;
  activo: boolean;
  created_at: string;
  updated_at: string;
};

type PlanForm = {
  codigo: string;
  nombre: string;
  descripcion: string;
  moneda: string;
  precio: string;
  periodo_dias: string;
  limite_usuarios: string;
  limite_sucursales: string;
  activo: boolean;
};

const DEFAULT_FORM: PlanForm = {
  codigo: '',
  nombre: '',
  descripcion: '',
  moneda: 'CLP',
  precio: '0',
  periodo_dias: '30',
  limite_usuarios: '',
  limite_sucursales: '',
  activo: true,
};

const toCode = (value: string) =>
  value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 50);

export default function SaasPlanesPage() {
  const [planes, setPlanes] = useState<SaasPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<SaasPlan | null>(null);
  const [codeTouched, setCodeTouched] = useState(false);
  const [form, setForm] = useState<PlanForm>(DEFAULT_FORM);

  const stats = useMemo(() => {
    const total = planes.length;
    const activos = planes.filter((p) => p.activo).length;
    const inactivos = total - activos;
    return { total, activos, inactivos };
  }, [planes]);

  const fetchPlanes = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data, error } = await supabase
        .from('saas_planes')
        .select('*')
        .order('precio', { ascending: true });

      if (error) throw error;
      setPlanes((data as SaasPlan[]) || []);
    } catch (err: any) {
      setError(err?.message || 'No se pudo cargar los planes SaaS');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPlanes();
  }, []);

  const openCreate = () => {
    setEditing(null);
    setCodeTouched(false);
    setForm(DEFAULT_FORM);
    setShowModal(true);
  };

  const openEdit = (plan: SaasPlan) => {
    setEditing(plan);
    setCodeTouched(true);
    setForm({
      codigo: plan.codigo,
      nombre: plan.nombre,
      descripcion: plan.descripcion || '',
      moneda: plan.moneda || 'CLP',
      precio: String(plan.precio ?? 0),
      periodo_dias: String(plan.periodo_dias ?? 30),
      limite_usuarios: plan.limite_usuarios == null ? '' : String(plan.limite_usuarios),
      limite_sucursales: plan.limite_sucursales == null ? '' : String(plan.limite_sucursales),
      activo: !!plan.activo,
    });
    setShowModal(true);
  };

  const onNameChange = (nombre: string) => {
    setForm((prev) => ({
      ...prev,
      nombre,
      codigo: codeTouched ? prev.codigo : toCode(nombre),
    }));
  };

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setSaving(true);
      setError(null);

      if (!form.nombre.trim()) {
        throw new Error('Nombre es obligatorio.');
      }

      const codigo = toCode(form.codigo || form.nombre);
      if (!codigo) {
        throw new Error('Codigo invalido.');
      }

      const periodo = Number(form.periodo_dias);
      if (!Number.isFinite(periodo) || periodo <= 0) {
        throw new Error('Periodo invalido.');
      }

      const precio = Number(form.precio);
      if (!Number.isFinite(precio) || precio < 0) {
        throw new Error('Precio invalido.');
      }

      const payload = {
        codigo,
        nombre: form.nombre.trim(),
        descripcion: form.descripcion.trim() || null,
        moneda: (form.moneda || 'CLP').trim().toUpperCase(),
        precio,
        periodo_dias: periodo,
        limite_usuarios: form.limite_usuarios.trim() ? Number(form.limite_usuarios) : null,
        limite_sucursales: form.limite_sucursales.trim() ? Number(form.limite_sucursales) : null,
        activo: !!form.activo,
      };

      if (editing) {
        const { error } = await supabase.from('saas_planes').update(payload).eq('id', editing.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from('saas_planes').insert([payload]);
        if (error) throw error;
      }

      setShowModal(false);
      setEditing(null);
      await fetchPlanes();
    } catch (err: any) {
      setError(err?.message || 'No se pudo guardar el plan');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (plan: SaasPlan) => {
    try {
      setError(null);
      const { error } = await supabase
        .from('saas_planes')
        .update({ activo: !plan.activo, updated_at: new Date().toISOString() })
        .eq('id', plan.id);
      if (error) throw error;
      await fetchPlanes();
    } catch (err: any) {
      setError(err?.message || 'No se pudo actualizar el estado del plan');
    }
  };

  return (
    <AdminLayout>
      <div className="p-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Planes SaaS</h1>
            <p className="text-gray-600 mt-1">Define los planes que los gimnasios te pagan por usar el sistema.</p>
          </div>
          <button
            onClick={openCreate}
            className="bg-teal-500 text-white px-6 py-3 rounded-lg hover:bg-teal-600 transition-colors flex items-center gap-2 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-add-line text-lg"></i>
            Nuevo Plan SaaS
          </button>
        </div>

        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
            <i className="ri-error-warning-line"></i>
            {error}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Stat title="Total" value={stats.total} />
          <Stat title="Activos" value={stats.activos} color="text-green-600" />
          <Stat title="Inactivos" value={stats.inactivos} color="text-gray-600" />
        </div>

        {loading ? (
          <div className="text-center py-20 text-gray-500">Cargando planes SaaS...</div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {planes.map((p) => (
              <div key={p.id} className="bg-white border border-gray-200 rounded-xl p-6">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900">{p.nombre}</h3>
                    <p className="text-sm text-gray-500 mt-1">Codigo: {p.codigo}</p>
                    <p className="text-sm text-gray-500">
                      {p.periodo_dias} dias · {p.moneda} ${Number(p.precio).toLocaleString('es-CL')}
                    </p>
                    {p.descripcion ? <p className="text-sm text-gray-600 mt-2">{p.descripcion}</p> : null}
                    <div className="mt-2 text-xs text-gray-500">
                      Limites:
                      {' '}
                      usuarios {p.limite_usuarios ?? 'ilimitado'} · sucursales {p.limite_sucursales ?? 'ilimitado'}
                    </div>
                  </div>
                  <span
                    className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                      p.activo ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                    }`}
                  >
                    {p.activo ? 'activo' : 'inactivo'}
                  </span>
                </div>

                <div className="flex items-center gap-2 mt-5 pt-4 border-t border-gray-200">
                  <button
                    onClick={() => openEdit(p)}
                    className="px-4 py-2 text-sm rounded-lg border border-gray-300 hover:bg-gray-50 cursor-pointer"
                  >
                    Editar
                  </button>
                  <button
                    onClick={() => toggleActive(p)}
                    className="px-4 py-2 text-sm rounded-lg border border-gray-300 hover:bg-gray-50 cursor-pointer"
                  >
                    {p.activo ? 'Desactivar' : 'Activar'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {showModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full">
              <div className="p-6 border-b border-gray-200">
                <h2 className="text-2xl font-bold text-gray-900">{editing ? 'Editar Plan SaaS' : 'Nuevo Plan SaaS'}</h2>
              </div>

              <form onSubmit={save} className="p-6 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Nombre *</label>
                    <input
                      required
                      value={form.nombre}
                      onChange={(e) => onNameChange(e.target.value)}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Codigo *</label>
                    <input
                      required
                      value={form.codigo}
                      onChange={(e) => {
                        setCodeTouched(true);
                        setForm((prev) => ({ ...prev, codigo: toCode(e.target.value) }));
                      }}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Descripcion</label>
                  <textarea
                    value={form.descripcion}
                    onChange={(e) => setForm((prev) => ({ ...prev, descripcion: e.target.value }))}
                    rows={2}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  />
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Moneda</label>
                    <input
                      value={form.moneda}
                      onChange={(e) => setForm((prev) => ({ ...prev, moneda: e.target.value.toUpperCase() }))}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Precio *</label>
                    <input
                      type="number"
                      min="0"
                      required
                      value={form.precio}
                      onChange={(e) => setForm((prev) => ({ ...prev, precio: e.target.value }))}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Periodo (dias) *</label>
                    <input
                      type="number"
                      min="1"
                      required
                      value={form.periodo_dias}
                      onChange={(e) => setForm((prev) => ({ ...prev, periodo_dias: e.target.value }))}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    />
                  </div>
                  <div className="flex items-end">
                    <label className="inline-flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={form.activo}
                        onChange={(e) => setForm((prev) => ({ ...prev, activo: e.target.checked }))}
                        className="w-4 h-4 text-teal-600 border-gray-300 rounded"
                      />
                      Activo
                    </label>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Limite usuarios</label>
                    <input
                      type="number"
                      min="1"
                      placeholder="Vacio = ilimitado"
                      value={form.limite_usuarios}
                      onChange={(e) => setForm((prev) => ({ ...prev, limite_usuarios: e.target.value }))}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Limite sucursales</label>
                    <input
                      type="number"
                      min="1"
                      placeholder="Vacio = ilimitado"
                      value={form.limite_sucursales}
                      onChange={(e) => setForm((prev) => ({ ...prev, limite_sucursales: e.target.value }))}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    />
                  </div>
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      setEditing(null);
                    }}
                    className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={saving}
                    className="flex-1 px-6 py-3 bg-teal-500 disabled:opacity-70 text-white rounded-lg hover:bg-teal-600 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    {saving ? 'Guardando...' : editing ? 'Guardar cambios' : 'Crear plan'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}

function Stat({ title, value, color = 'text-gray-900' }: { title: string; value: number; color?: string }) {
  return (
    <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
      <p className="text-gray-600 text-sm">{title}</p>
      <p className={`text-3xl font-bold mt-1 ${color}`}>{value}</p>
    </div>
  );
}
